import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { EventDTO } from '../../types';
import { fetchEventsForSchoolYear } from '../../api/events';
import { amountTone, formatMoney } from '../../utils/format';
import { PaymentLegend, StatusBadge } from './StatusBadge';

interface PublicEventStatus {
    eventName: string;
    paid: boolean;
    ignored: boolean;
    frozen: boolean;
}

interface PublicPaymentSummary {
    childName: string;
    totalAmount: number;
    eventStatuses: PublicEventStatus[];
    saldo: number;
}

const PublicPaymentSummaryTable: React.FC<{ secret: string, startDate: string | null, endDate: string | null, schoolYear: string }> = ({ secret, startDate, endDate, schoolYear }) => {
    const [summaryData, setSummaryData] = useState<PublicPaymentSummary[]>([]);
    const [events, setEvents] = useState<string[]>([]);
    const [eventDetails, setEventDetails] = useState<EventDTO[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (startDate && endDate && schoolYear && secret) {
            const params = { startDate, endDate, schoolYear };
            axios.get<PublicPaymentSummary[]>(`/api/public/${secret}/summary`, { params })
                .then(response => {
                    setSummaryData(response.data);
                    if (response.data.length > 0) {
                        const eventNames = response.data[0].eventStatuses.map(es => es.eventName);
                        setEvents(eventNames);

                        fetchEventsForSchoolYear(schoolYear, `/api/public/${secret}`)
                            .then(details => { if (details) setEventDetails(details); });
                    }
                })
                .catch(err => {
                    if (err.response && err.response.status === 403) {
                        setError("Odmowa dostępu. Nieprawidłowy link.");
                    } else {
                        setError("Wystąpił błąd podczas pobierania danych.");
                    }
                });
        }
    }, [startDate, endDate, schoolYear, secret]);

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    const totalIncome = summaryData.reduce((sum, row) => sum + row.totalAmount, 0);

    const paidCount = events.map(event => {
        const count = summaryData.filter(row => {
            const eventStatus = row.eventStatuses.find(es => es.eventName === event);
            return eventStatus && eventStatus.paid;
        }).length;

        return { eventName: event, count };
    });

    const sumOfPayments = events.map(event => {
        const eventDetail = eventDetails.find(e => e.name === event);
        if (!eventDetail) return { eventName: event, sum: 0 };

        const paidUsers = summaryData.filter(row => {
            const eventStatus = row.eventStatuses.find(es => es.eventName === event);
            return eventStatus && eventStatus.paid;
        }).length;

        return { eventName: event, sum: paidUsers * eventDetail.amount };
    });

    return (
        <section className="section">
            <h2 className="section-title">Podsumowanie płatności</h2>
            <div className="surface table-card">
                <PaymentLegend />
                <div className="table-scroll">
                    <table className="table data-table matrix">
                        <thead>
                            <tr>
                                <th className="col-lp" scope="col">Lp.</th>
                                <th className="col-name" scope="col">Nazwa</th>
                                {events.map(event => {
                                    const details = eventDetails.find(e => e.name === event);
                                    return (
                                        <th key={event} className="event-col" scope="col">
                                            <div className="event-head">
                                                <span className="event-name" title={event}>{event}</span>
                                                {details && <span className="event-amount">{formatMoney(details.amount)}</span>}
                                            </div>
                                        </th>
                                    );
                                })}
                                <th className="num" scope="col">Przychody</th>
                                <th className="num" scope="col">Saldo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summaryData.map((row, index) => (
                                <tr key={index} className={amountTone(row.saldo) === 'negative' ? 'row-debt' : undefined}>
                                    <td className="col-lp">{index + 1}</td>
                                    <td className="col-name">{row.childName}</td>
                                    {row.eventStatuses.map((status, idx) => (
                                        <td key={idx} className="status-cell">
                                            <StatusBadge
                                                state={status.paid ? 'paid' : 'unpaid'}
                                                ariaLabel={`${row.childName}, ${status.eventName}: ${status.paid ? 'wpłacono' : 'brak wpłaty'}`}
                                            />
                                        </td>
                                    ))}
                                    <td className="num">{formatMoney(row.totalAmount)}</td>
                                    <td className={`num tone-${amountTone(row.saldo)}`}>{formatMoney(row.saldo, { signed: true })}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td className="col-lp"></td>
                                <td className="col-name">Wpłacili</td>
                                {paidCount.map(pc => (
                                    <td key={pc.eventName} className="center">
                                        {pc.count}<span className="count-of">/{summaryData.length}</span>
                                    </td>
                                ))}
                                <td></td>
                                <td></td>
                            </tr>
                            <tr>
                                <td className="col-lp"></td>
                                <td className="col-name">Suma</td>
                                {sumOfPayments.map(sp => (
                                    <td key={sp.eventName} className="center nowrap">{formatMoney(sp.sum)}</td>
                                ))}
                                <td className="num">{formatMoney(totalIncome)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </section>
    );
};

export default PublicPaymentSummaryTable;
